import { useEffect, useMemo, useState } from 'react'
import { SlideOver } from './SlideOver.js'
import { createQuote, createQuoteFromTemplate, fetchTemplates, type TemplateRow } from '../lib/api.js'
import { SupplierQuoteImport } from './SupplierQuoteImport.js'

/**
 * Decision 210 (Mark, 2026-09-09): "+ New quote" (cockpit and Quotes list)
 * offers the choice — a blank quote, or one built from a template — instead
 * of always starting blank. The template path is the Templates page's own
 * (`createQuoteFromTemplate`: repriced from today's catalogue, skipped /
 * ad-hoc / retired-terms notes surfaced), so the two entry points cannot
 * drift. Retired templates are not offered here.
 */
export function NewQuoteChooser({ onClose, onCreated, onError, supplierImport = false }: {
  onClose: () => void
  /** Decision 211: staff on the internal partner also get "from a distributor quote". */
  supplierImport?: boolean
  /** The new quote's id and a one-line notice for the page to show (template notes). */
  onCreated: (quoteId: string, notice: string | null) => void
  onError: (message: string) => void
}) {
  const [templates, setTemplates] = useState<TemplateRow[] | null>(null)
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState<string | null>(null)   // 'blank' | template id
  const [supplier, setSupplier] = useState(false)          // decision 211: the import panel

  useEffect(() => {
    fetchTemplates().then((r) => setTemplates(r.templates.filter((t) => t.active))).catch((e: Error) => { setTemplates([]); onError(e.message) })
  }, [onError])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const list = (templates ?? []).filter((t) => !needle || t.name.toLowerCase().includes(needle) || t.summary.toLowerCase().includes(needle))
    // Wave9-global first, then the partner's own; alphabetical inside each.
    return list.sort((a, b) => Number(a.partnerId !== null) - Number(b.partnerId !== null) || a.name.localeCompare(b.name))
  }, [templates, q])

  const blank = async () => {
    setBusy('blank')
    try { const c = await createQuote(); onCreated(c.id, null) }
    catch (e) { onError((e as Error).message) } finally { setBusy(null) }
  }
  const fromTemplate = async (t: TemplateRow) => {
    setBusy(t.id)
    try {
      const res = await createQuoteFromTemplate(t.id)
      const notes: string[] = []
      if (res.skipped.length > 0) notes.push(`skipped ${res.skipped.length} line(s) — inactive, or not available in this partner's mode: ${res.skipped.join(', ')}`)
      if (res.adhocCarried.length > 0) notes.push(`review the ad-hoc price(s) on: ${res.adhocCarried.join(', ')}`)
      if (res.retiredTerms) notes.push(`this template's T&Cs (${res.retiredTerms}) are retired — the quote has none, pick a set in the Send panel`)
      // The builder opens next, so a page banner would never be seen — anything
      // that needs a human's eye (skipped lines, ad-hoc prices, retired T&Cs)
      // is said HERE, before the quote opens.
      if (notes.length > 0) window.alert(`${res.quoteNumber} created from “${res.templateName}” — please review:\n\n• ${notes.join('\n• ')}`)
      onCreated(res.id, `Created ${res.quoteNumber} from “${res.templateName}” — repriced from today's catalogue${notes.length > 0 ? '. ⚠ ' + notes.join(' · ') : '.'}`)
    } catch (e) { onError((e as Error).message) } finally { setBusy(null) }
  }

  if (supplier) {
    return <SupplierQuoteImport onBack={() => setSupplier(false)} onClose={onClose} onCreated={onCreated} onError={onError} />
  }

  return (
    <SlideOver title="New quote" subtitle="Start blank, or from a template — a template quote is repriced from today's catalogue" onClose={onClose}>
      <button className="btn btn--primary" style={{ width: '100%', marginBottom: 14 }} disabled={busy !== null} onClick={() => void blank()} data-testid="new-blank">
        {busy === 'blank' ? 'Creating…' : '+ Blank quote'}
      </button>
      {supplierImport && (
        <button className="btn" style={{ width: '100%', marginBottom: 14 }} disabled={busy !== null} onClick={() => setSupplier(true)} data-testid="new-supplier"
          title="One-off Meraki sales: upload the distributor's quote — their unit price is our cost, you set the margin, missing SKUs are created in HubSpot">
          ↑ From a distributor quote (Ingram Micro .xlsx)
        </button>
      )}
      <h3 style={{ margin: '6px 0 8px' }}>From a template</h3>
      {templates && templates.length > 3 && (
        <input className="input" style={{ width: '100%', marginBottom: 10 }} placeholder="Search templates…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search templates" />
      )}
      {templates === null && <p className="muted">Loading templates…</p>}
      {templates && templates.length === 0 && <p className="muted">No templates yet — save one from any quote with “Save as template”.</p>}
      {templates && templates.length > 0 && shown.length === 0 && <p className="muted">Nothing matches “{q}”.</p>}
      <div className="table-wrap">
        <table data-testid="template-choices">
          <tbody>
            {shown.map((t) => (
              <tr key={t.id}>
                <td>
                  <strong>{t.name}</strong>{' '}
                  {t.partnerId === null && <span className="pill pill--brand">Wave9 global</span>}{' '}
                  {t.adhocLines > 0 && <span className="pill pill--warn" title="Ad-hoc lines carry the prices saved in the template — review them on the new quote.">{t.adhocLines} ad-hoc</span>}
                  <div className="muted small">{t.summary}{t.carries.label ? ` · ${t.carries.label}` : ''}</div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn--sm" disabled={busy !== null} onClick={() => void fromTemplate(t)}>{busy === t.id ? 'Creating…' : 'Use'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SlideOver>
  )
}
